import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import Chatbot from '@/components/shared/Chatbot';
import { Button } from '@/components/ui/button';
import { AuthContext } from '@/App';
import { toast } from 'sonner';
import { 
  ArrowRight, Award, Users, Briefcase, GraduationCap, Building2, 
  CheckCircle, BookOpen, FileText, Trophy, Star, Quote, 
  UserPlus, ClipboardList, PlayCircle, FileCheck, BadgeCheck, Search,
  IndianRupee, MapPin, Phone, Mail, ChevronRight
} from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useContext(AuthContext);

  // Handle Enroll Now click
  const handleEnrollClick = () => {
    if (isAuthenticated && user) {
      if (user.role === 'student') {
        toast.success('Redirecting to your dashboard...');
        navigate('/student/dashboard?tab=courses');
      } else {
        toast.error('Only students can enroll in courses');
      }
    } else {
      toast.info('Please login to enroll in courses');
      navigate('/auth/login?redirect=/courses');
    }
  };

  // Stats for Trust Bar
  const trustStats = [
    { value: '25,000+', label: 'Students Trained' },
    { value: '11,500+', label: 'Placements Done' },
    { value: '100+', label: 'Training Centers' },
    { value: 'Govt.', label: 'Affiliated Programs' }
  ];

  // How It Works Steps
  const studentSteps = [
    { icon: UserPlus, title: 'Sign Up', desc: 'Create your account' },
    { icon: BookOpen, title: 'Choose Course', desc: 'Pick from 100+ courses' },
    { icon: PlayCircle, title: 'Learn', desc: 'Online or at Center' },
    { icon: ClipboardList, title: 'Give Exam', desc: 'Online MCQ test' },
    { icon: BadgeCheck, title: 'Get Certificate', desc: 'Download instantly' },
    { icon: Briefcase, title: 'Apply for Jobs', desc: 'Get placed' }
  ];

  // Popular Courses - Matching with Courses Page
  const popularCourses = [
    { name: 'Technical Courses', icon: '🔧', courses: ['AutoCAD', 'CIVIL CAD', '3ds Max', 'Interior Design', 'Land Surveyor'] },
    { name: 'IT & Digital', icon: '💻', courses: ['Digital Marketing', 'Web Designer', 'Data Entry Operator'] },
    { name: 'Accounting', icon: '📊', courses: ['Tally with GST', 'Financial Accounting'] },
    { name: 'Advanced Tech', icon: '🤖', courses: ['AI Data Science', 'Python Programming', 'MS Office Advanced'] }
  ];

  // Placement Success Stories - Without Photos
  const placements = [
    { name: 'Rahul Kumar', role: 'Retail Executive', company: 'Reliance Retail', salary: '₹15,000/month' },
    { name: 'Priya Sharma', role: 'Data Entry Operator', company: 'TCS', salary: '₹18,000/month' },
    { name: 'Amit Singh', role: 'AutoCAD Designer', company: 'L&T', salary: '₹22,000/month' },
    { name: 'Neha Patel', role: 'Healthcare Assistant', company: 'Apollo Hospital', salary: '₹16,000/month' }
  ];

  // Testimonials - Without Photos
  const testimonials = [
    { name: 'Ravi Verma', role: 'Student', text: 'Iqra Skills ne meri zindagi badal di. Maine computer course kiya aur ab ₹20,000/month kama raha hoon.' },
    { name: 'Sunita Devi', role: 'Franchise Owner', text: 'Maine Iqra Skills franchise liya aur ab apna business chal raha hai. Full support milta hai.' },
    { name: 'Mohammad Irfan', role: 'Student', text: 'Government affiliated courses hain, certificate bhi genuine milta hai. Bahut accha experience raha.' }
  ];

  // Partner Logos
  const partnerLogos = [
    { name: 'NSDC', img: 'https://www.nsdcindia.org/sites/default/files/NSDC-logo.png' },
    { name: 'Skill India', img: 'https://upload.wikimedia.org/wikipedia/en/thumb/5/5d/Skill_India.svg/1200px-Skill_India.svg.png' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* ============================================ */}
      {/* 1. HERO SECTION */}
      {/* ============================================ */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-emerald-700 py-20 lg:py-28" data-testid="hero-section">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="hero-headline">
              Empowering Skills.<br />
              <span className="text-emerald-400">Creating Careers.</span><br />
              Building Businesses.
            </h1>
            
            {/* Subheading */}
            <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
              Join India's fastest growing skill development network. <br />
              <span className="font-semibold text-white">Learn, Earn & Grow</span> with Iqra Skills.
            </p>
            
            {/* 3 Primary CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <Button 
                onClick={() => navigate('/courses')} 
                className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                data-testid="cta-student"
              >
                <GraduationCap className="mr-2 h-6 w-6" />
                I am a Student
              </Button>
              <Button 
                onClick={() => navigate('/partner')} 
                className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 text-white rounded-xl px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                data-testid="cta-franchise"
              >
                <Building2 className="mr-2 h-6 w-6" />
                Become a Partner
              </Button>
              <Button 
                onClick={() => navigate('/auth/register')} 
                className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white rounded-xl px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                data-testid="cta-employer"
              >
                <Briefcase className="mr-2 h-6 w-6" />
                Hire Skilled Students
              </Button>
            </div>
            
            {/* Secondary CTA */}
            <Button 
              variant="ghost" 
              onClick={() => navigate('/courses')} 
              className="text-white hover:text-emerald-300 hover:bg-white/10 text-lg"
              data-testid="cta-explore"
            >
              <Search className="mr-2 h-5 w-5" />
              Explore Courses
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* 2. TRUST BAR */}
      {/* ============================================ */}
      <section className="py-8 bg-slate-50 border-y border-slate-200" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 items-center">
            {trustStats.map((stat, index) => (
              <div key={index} className="flex items-center justify-center gap-3 text-center md:text-left">
                <CheckCircle className="h-6 w-6 text-emerald-500 flex-shrink-0" />
                <div>
                  <span className="text-2xl font-bold text-slate-900">{stat.value}</span>
                  <span className="block text-sm text-slate-600">{stat.label}</span>
                </div>
              </div>
            ))}
          </div>
          
          {/* Partner Logos */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <div className="text-center mb-4">
              <span className="text-lg font-semibold text-slate-700">Recognized & Affiliated</span>
              <p className="text-sm text-slate-500 mt-1">Skill India | NSDC | MSDE (NAPS) | ESSCI | TSSC | MSME | Startup India</p>
            </div>
            <div className="flex flex-wrap justify-center items-center gap-6 md:gap-8">
              <img src="https://customer-assets.emergentagent.com/job_2f370b5d-868a-4240-92b5-92a3302f8c5a/artifacts/rvpu7cjo_Untitled-1%201.png" alt="Skill India" className="h-12 md:h-14 object-contain" />
              <img src="https://customer-assets.emergentagent.com/job_2f370b5d-868a-4240-92b5-92a3302f8c5a/artifacts/ecr6mldc_nsdc.png" alt="NSDC" className="h-12 md:h-14 object-contain" />
              <img src="https://customer-assets.emergentagent.com/job_2f370b5d-868a-4240-92b5-92a3302f8c5a/artifacts/27q4pw5s_msde.png" alt="MSDE" className="h-12 md:h-14 object-contain" />
              <img src="https://customer-assets.emergentagent.com/job_2f370b5d-868a-4240-92b5-92a3302f8c5a/artifacts/73ilo1nv_ESSCI.png" alt="ESSCI" className="h-12 md:h-14 object-contain" />
              <img src="https://customer-assets.emergentagent.com/job_2f370b5d-868a-4240-92b5-92a3302f8c5a/artifacts/cvke5mxt_TSSC.png" alt="TSSC" className="h-12 md:h-14 object-contain" />
              <img src="https://customer-assets.emergentagent.com/job_2f370b5d-868a-4240-92b5-92a3302f8c5a/artifacts/j0rxpcx5_MSME.png" alt="MSME" className="h-12 md:h-14 object-contain" />
              <img src="https://customer-assets.emergentagent.com/job_2f370b5d-868a-4240-92b5-92a3302f8c5a/artifacts/1lnj5n0x_PMKVY.png" alt="PMKVY" className="h-12 md:h-14 object-contain" />
              <img src="https://customer-assets.emergentagent.com/job_2f370b5d-868a-4240-92b5-92a3302f8c5a/artifacts/cpu7r35q_SIDH.jpg" alt="SIDH" className="h-12 md:h-14 object-contain" />
              <img src="https://customer-assets.emergentagent.com/job_2f370b5d-868a-4240-92b5-92a3302f8c5a/artifacts/in2xdp7a_STARTUP.png" alt="Startup India" className="h-12 md:h-14 object-contain" />
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* 3. HOW IT WORKS */}
      {/* ============================================ */}
      <section className="py-16 bg-white" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">How It Works</h2>
            <p className="text-lg text-slate-600">Your journey from learning to earning in 6 simple steps</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {studentSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="relative text-center group">
                  {/* Connector Line */}
                  {index < studentSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-8 left-1/2 w-full h-0.5 bg-emerald-200"></div>
                  )}
                  
                  <div className="relative z-10 bg-white">
                    <div className="w-16 h-16 mx-auto bg-gradient-to-br from-emerald-500 to-blue-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform">
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <div className="text-xs font-bold text-emerald-600 mb-1">Step {index + 1}</div>
                    <h3 className="font-semibold text-slate-900 mb-1">{step.title}</h3>
                    <p className="text-sm text-slate-500">{step.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* 4. STUDENT SECTION */}
      {/* ============================================ */}
      <section className="py-16 bg-gradient-to-r from-emerald-600 to-blue-600" data-testid="student-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h2 className="text-4xl font-bold mb-6">Start Your Career Today</h2>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-emerald-300" />
                  <span className="text-lg">100+ Job-oriented courses</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-emerald-300" />
                  <span className="text-lg">Online & Offline options</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-emerald-300" />
                  <span className="text-lg">Placement assistance included</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-emerald-300" />
                  <span className="text-lg">Government recognized Certificates, Diplomas & Degrees</span>
                </li>
              </ul>
              <Button 
                onClick={handleEnrollClick} 
                className="bg-white text-emerald-600 hover:bg-emerald-50 rounded-xl px-8 py-6 text-lg font-semibold"
                data-testid="student-enroll-btn"
              >
                <GraduationCap className="mr-2 h-6 w-6" />
                Enroll Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600" 
                alt="Students learning"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-4 shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                    <Trophy className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">25,000+</p>
                    <p className="text-sm text-slate-500">Students Trained</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* 5. FRANCHISE SECTION */}
      {/* ============================================ */}
      <section className="py-16 bg-amber-50" data-testid="franchise-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=600" 
                alt="Training Center"
                className="rounded-2xl shadow-xl"
              />
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-block bg-amber-200 text-amber-800 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                💰 Business Opportunity
              </div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">Start Your Own Skill Center</h2>
              <p className="text-lg text-slate-600 mb-6">
                Low investment, high earning potential. Join India's largest skill training network.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <CheckCircle className="h-6 w-6 text-emerald-500 mb-2" />
                  <p className="font-semibold text-slate-900">Govt. Recognized</p>
                  <p className="text-sm text-slate-500">Certificates, Diplomas & Degrees</p>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <CheckCircle className="h-6 w-6 text-emerald-500 mb-2" />
                  <p className="font-semibold text-slate-900">Industry Supported</p>
                  <p className="text-sm text-slate-500">Job-oriented Courses</p>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <CheckCircle className="h-6 w-6 text-emerald-500 mb-2" />
                  <p className="font-semibold text-slate-900">70% Revenue</p>
                  <p className="text-sm text-slate-500">Keep 70% of course fees</p>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <CheckCircle className="h-6 w-6 text-emerald-500 mb-2" />
                  <p className="font-semibold text-slate-900">Full Placement Support</p>
                  <p className="text-sm text-slate-500">Training & Marketing</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/partner')} 
                className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl px-8 py-6 text-lg font-semibold"
                data-testid="franchise-apply-btn"
              >
                <Building2 className="mr-2 h-6 w-6" />
                Become a Partner
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* 6. EMPLOYER SECTION */}
      {/* ============================================ */}
      <section className="py-16 bg-blue-900" data-testid="employer-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h2 className="text-4xl font-bold mb-6">Hire Skilled Candidates Easily</h2>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-blue-300" />
                  <span className="text-lg">Access 25,000+ trained students</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-blue-300" />
                  <span className="text-lg">Filter by skill, location & experience</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-blue-300" />
                  <span className="text-lg">Post jobs & hire directly</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-blue-300" />
                  <span className="text-lg">Schedule interviews online</span>
                </li>
              </ul>
              <Button 
                onClick={() => navigate('/jobs')} 
                className="bg-white text-blue-900 hover:bg-blue-50 rounded-xl px-8 py-6 text-lg font-semibold"
                data-testid="employer-post-job-btn"
              >
                <Briefcase className="mr-2 h-6 w-6" />
                Post a Job
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/10 backdrop-blur rounded-xl p-6 text-center text-white">
                <Users className="h-10 w-10 mx-auto mb-3 text-blue-300" />
                <p className="text-3xl font-bold">25,000+</p>
                <p className="text-sm text-blue-200">Trained Candidates</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-6 text-center text-white">
                <Briefcase className="h-10 w-10 mx-auto mb-3 text-blue-300" />
                <p className="text-3xl font-bold">11,500+</p>
                <p className="text-sm text-blue-200">Placements Done</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-6 text-center text-white">
                <Building2 className="h-10 w-10 mx-auto mb-3 text-blue-300" />
                <p className="text-3xl font-bold">100+</p>
                <p className="text-sm text-blue-200">Partner Companies</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-6 text-center text-white">
                <Award className="h-10 w-10 mx-auto mb-3 text-blue-300" />
                <p className="text-3xl font-bold">50+</p>
                <p className="text-sm text-blue-200">Skill Courses</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* 7. COURSES SECTION */}
      {/* ============================================ */}
      <section className="py-16 bg-white" data-testid="courses-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Popular Courses</h2>
            <p className="text-lg text-slate-600">Choose from 100+ job-oriented courses</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {popularCourses.map((category, index) => (
              <div key={index} className="bg-slate-50 rounded-2xl p-6 hover:shadow-lg transition-shadow cursor-pointer group" onClick={() => navigate('/courses')}>
                <div className="text-4xl mb-4">{category.icon}</div>
                <h3 className="font-bold text-lg text-slate-900 mb-3 group-hover:text-blue-600 transition-colors">
                  {category.name}
                </h3>
                <ul className="space-y-2">
                  {category.courses.map((course, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                      <ChevronRight className="h-4 w-4 text-emerald-500" />
                      {course}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <Button 
              onClick={() => navigate('/courses')} 
              variant="outline"
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 rounded-xl px-8 py-4 text-lg"
            >
              View All Courses
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* 8. PLACEMENT PROOF */}
      {/* ============================================ */}
      <section className="py-16 bg-slate-50" data-testid="placement-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Our Students Are Working With</h2>
            <p className="text-lg text-slate-600">Real success stories from our trained candidates</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {placements.map((student, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow text-center">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center">
                  <span className="text-white font-bold text-xl">{student.name.charAt(0)}</span>
                </div>
                <h3 className="font-bold text-slate-900 mb-1">{student.name}</h3>
                <p className="text-emerald-600 font-semibold mb-1">{student.salary}</p>
                <p className="text-sm text-slate-600">{student.role}</p>
                <p className="text-sm text-slate-500">{student.company}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* 9. TESTIMONIALS */}
      {/* ============================================ */}
      <section className="py-16 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">What People Say</h2>
            <p className="text-lg text-slate-600">Hear from our students and franchise partners</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-slate-50 rounded-2xl p-6 relative">
                <Quote className="h-10 w-10 text-emerald-200 absolute top-4 right-4" />
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center">
                    <span className="text-white font-bold text-lg">{testimonial.name.charAt(0)}</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">{testimonial.name}</h3>
                    <p className="text-sm text-emerald-600">{testimonial.role}</p>
                  </div>
                </div>
                <p className="text-slate-600 italic">"{testimonial.text}"</p>
                <div className="flex gap-1 mt-4">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* 10. FINAL CTA */}
      {/* ============================================ */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-emerald-800" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Start Your Journey?</h2>
          <p className="text-xl text-blue-100 mb-10">
            Join thousands of students, franchise owners, and employers who trust Iqra Skills
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              onClick={handleEnrollClick} 
              className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl px-8 py-6 text-lg font-semibold shadow-lg"
              data-testid="final-cta-student"
            >
              <GraduationCap className="mr-2 h-6 w-6" />
              Join as Student
            </Button>
            <Button 
              onClick={() => navigate('/partner')} 
              className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 text-white rounded-xl px-8 py-6 text-lg font-semibold shadow-lg"
              data-testid="final-cta-franchise"
            >
              <Building2 className="mr-2 h-6 w-6" />
              Become a Partner
            </Button>
            <Button 
              onClick={() => navigate('/auth/register')} 
              className="w-full sm:w-auto bg-white text-blue-900 hover:bg-blue-50 rounded-xl px-8 py-6 text-lg font-semibold shadow-lg"
              data-testid="final-cta-employer"
            >
              <Briefcase className="mr-2 h-6 w-6" />
              Hire Now
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <Chatbot />
    </div>
  );
};

export default HomePage;
